import useGetSubscription from "hooks/services/subscription/useGetSubscription";
import {
  createContext,
  FC,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import { Subscription } from "types/subscription";
import { SubscriptionSearchParameters } from "types/subscriptionSearchParameters";
import { Constants } from "utils/constants/constants";
import { SubscriptionContextType } from "./types";

export const SubscriptionContext = createContext<
  SubscriptionContextType | undefined
>(undefined);

SubscriptionContext.displayName = "SubscriptionContext";

const SubscriptionProvider: FC = ({ children }) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<
    number | null
  >(null);
  const [searchParameters, setSearchParameters] =
    useState<SubscriptionSearchParameters>({
      limit: Constants.defaultLimit,
      name: null,
      subscriptionId: null,
      userId: null,
      type: null,
    });
  const [searchText, setSearchText] = useState<string>("");
  const [userIdFilter, setUserIdFilterValue] = useState<string>("");

  // The backend binds userId as an int, so non-numeric input would fail the request
  const setUserIdFilter = useCallback((value: string) => {
    setUserIdFilterValue(value.replace(/\D/g, ""));
  }, []);

  const {
    data: subscriptionData,
    isRefetching,
    isFetching,
  } = useGetSubscription(
    selectedSubscriptionId ?? undefined,
    true,
    searchText,
    userIdFilter,
  );

  const isLoadingSubscription =
    !!selectedSubscriptionId &&
    isFetching &&
    (!subscription || subscription.id !== selectedSubscriptionId);

  useEffect(() => {
    if (isRefetching) return;
    if (subscriptionData && subscriptionData.data) {
      setSubscription(subscriptionData.data);
    }
  }, [subscriptionData, isRefetching]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        setSubscription,
        searchParameters,
        setSearchParameters,
        selectedSubscriptionId,
        setSelectedSubscriptionId,
        searchText,
        setSearchText,
        userIdFilter,
        setUserIdFilter,
        isLoadingSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionProvider;

export function useSelectedSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(`useAccount must be used within a AccountProvider`);
  }

  return context;
}
